import React, { useState } from 'react';
import { DropdownMenu } from '@spark';
import {
    ContactUsTooltip,
    CONTACT_US_AGENTIC,
    intentForAutonomyLevel,
    useSaasGating,
} from '@/features/customer-intents';
import { HelpIcon } from '../shared/TaskTypeControls';
import {
    AUTONOMY_LEVEL_HELP,
    AUTONOMY_LEVEL_OPTIONS,
    autonomyLevelLabel,
} from './autonomyLevels';

/**
 * Required "Autonomy Level" single-select on the Business Goals step (Figma
 * 3926-9563, Input Single Select): label + asterisk + help icon, placeholder
 * "Select...", four fixed options (1–4). The trigger fills its 368px wrapper
 * (slightly wider than the Figma w-360) so the longest option label is never
 * ellipsized in the panel or in the selected value.
 *
 * On SaaS only "no tool calling" plus the agentic levels a god enabled for
 * the workspace are self-serve (Figma 9035-9521): the other levels are
 * disabled with a "Contact us to enable agentic capabilities" tooltip whose
 * link closes the menu and calls `onContactUs` with the clicked level
 * pre-selected for the customer-intents modal.
 */
const AutonomyLevelSelect = ({ value, onChange, error, required = true, onContactUs }) => {
    const [open, setOpen] = useState(false);
    const selectedLabel = autonomyLevelLabel(value);
    const { contactUs, isAutonomyLevelAllowed } = useSaasGating(onContactUs);
    const openContactUs = contactUs
        ? (intent) => {
              setOpen(false);
              contactUs(intent);
          }
        : undefined;

    const toItem = (o) => {
        const selected = o.value === value;
        if (selected || isAutonomyLevelAllowed(o.value)) {
            return {
                label: o.label,
                state: selected ? 'selected' : 'default',
                onClick: () => {
                    onChange(o.value);
                    setOpen(false);
                },
            };
        }
        return {
            label: (
                <ContactUsTooltip
                    text={CONTACT_US_AGENTIC}
                    onContactUs={openContactUs}
                    intent={intentForAutonomyLevel(o.value)}
                    placement="right"
                    className="cw-autonomy-level-section__gated-option"
                >
                    <span>{o.label}</span>
                </ContactUsTooltip>
            ),
            state: 'disabled',
        };
    };

    return (
        <div className="cw-autonomy-level-section" data-testid="autonomy-level-select">
            <div className="cw-section-label">
                Autonomy Level
                {required && <span className="cw-required-asterisk">*</span>}
                <HelpIcon text={AUTONOMY_LEVEL_HELP} />
            </div>
            <div className="cw-autonomy-level-section__select">
                <DropdownMenu
                    selectType="single-select"
                    showLabel={false}
                    size="medium"
                    fill
                    placeholder="Select..."
                    text={selectedLabel || undefined}
                    panelLayout="overlay"
                    open={open}
                    onOpenChange={setOpen}
                    items={AUTONOMY_LEVEL_OPTIONS.map(toItem)}
                />
            </div>
            {error && <div className="cw-field-error">{error}</div>}
        </div>
    );
};

export default AutonomyLevelSelect;
